import { CheckCircle2, Eye, EyeOff, Globe2, KeyRound, Save } from 'lucide-react';
import { FormEvent, useEffect, useId, useState } from 'react';
import { Loading } from '../components/Loading';
import { api } from '../lib/api';
import type { PagaquiConfigStatus } from '../lib/types';

function CredentialField({
  label,
  defined,
  value,
  onChange,
}: {
  label: string;
  defined: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  return (
    <div className="credential-field">
      <div className="credential-field__header">
        <label htmlFor={id}>{label}</label>
        <span className={`status status--${defined ? 'active' : 'unavailable'}`}>
          <span className="status__dot" />
          {defined ? 'Definida' : 'Não definida'}
        </span>
      </div>
      <div className="pagaqui-secret">
      <input
        id={id}
        aria-describedby={`${id}-hint`}
        placeholder={defined ? 'Introduzir novo valor para alterar' : 'Introduzir credencial'}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
      />
      <button type="button" className="icon-button" aria-label={`${visible ? 'Ocultar' : 'Mostrar'} ${label}`} aria-pressed={visible} onClick={() => setVisible(!visible)}>{visible ? <EyeOff size={18} /> : <Eye size={18} />}</button>
      </div>
      <p id={`${id}-hint`} className="credential-field__hint">
        {defined
          ? 'Deixa em branco para manter o valor atual.'
          : 'Ainda não foi definida.'}
      </p>
    </div>
  );
}

export function PagaquiSettingsPage() {
  const [status, setStatus] = useState<PagaquiConfigStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const [authUrl, setAuthUrl] = useState('');
  const [productsUrl, setProductsUrl] = useState('');
  const [transactionUrl, setTransactionUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [terminalKey, setTerminalKey] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = () =>
    api<PagaquiConfigStatus>('/vouchers/pagaqui/config')
      .then((data) => {
        setStatus(data);
        setAuthUrl(data.authUrl);
        setProductsUrl(data.productsUrl);
        setTransactionUrl(data.transactionUrl);
        setUsername(data.username);
      })
      .catch(() => setError('Não foi possível carregar a configuração. Tenta novamente.'))
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const body: Record<string, string> = {
        authUrl,
        productsUrl,
        transactionUrl,
        username,
      };
      if (password) body.password = password;
      if (terminalKey) body.terminalKey = terminalKey;

      const updated = await api<PagaquiConfigStatus>('/vouchers/pagaqui/config', {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      setStatus(updated);
      setUsername(updated.username);
      setPassword('');
      setTerminalKey('');
      setSaved(true);
    } catch {
      setError('Não foi possível guardar a configuração.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page pagaqui-settings-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Pagaqui</span>
          <h1>Configurações</h1>
          <p>URLs e credenciais usados para autenticar e comunicar com a Pagaqui.</p>
        </div>
      </div>
      {loading ? <Loading /> : !status ? (
        <section className="panel pagaqui-load-error"><p role="alert">{error}</p><button className="button button--secondary" onClick={() => { setLoading(true); setError(''); void load(); }}>Tentar novamente</button></section>
      ) : (
        <form className="pagaqui-settings-form" onSubmit={submit} onChange={() => setSaved(false)}>
          <fieldset disabled={busy} className="pagaqui-settings-grid">
            <section className="panel pagaqui-config-card">
              <div className="pagaqui-config-heading"><span className="pagaqui-config-icon"><Globe2 size={22} /></span><div><h2>Ligação à Pagaqui</h2><p>Endereços dos serviços utilizados pela integração.</p></div></div>
              <div className="pagaqui-config-fields">
                <label>Autenticação <span className="pagaqui-field-caption">Auth URL</span>
                  <input type="url" value={authUrl} onChange={e => setAuthUrl(e.target.value)} required spellCheck={false} />
                  <small>URL completo do serviço de autenticação.</small>
                </label>
                <label>Catálogo de produtos <span className="pagaqui-field-caption">Products URL</span>
                  <input type="url" value={productsUrl} onChange={e => setProductsUrl(e.target.value)} required spellCheck={false} />
                  <small>Consulta os vouchers disponíveis e o respetivo stock.</small>
                </label>
                <label>Transações <span className="pagaqui-field-caption">Transaction URL</span>
                  <input type="url" value={transactionUrl} onChange={e => setTransactionUrl(e.target.value)} required spellCheck={false} />
                  <small>Processa as compras de vouchers.</small>
                </label>
              </div>
            </section>
            <section className="panel pagaqui-config-card">
              <div className="pagaqui-config-heading"><span className="pagaqui-config-icon"><KeyRound size={22} /></span><div><h2>Credenciais de acesso</h2><p>Dados de autenticação associados ao terminal.</p></div></div>
              <div className="pagaqui-config-fields">
                <label>Nome de utilizador <span className="pagaqui-field-caption">Username</span>
                  <input value={username} onChange={e => setUsername(e.target.value)} autoComplete="off" spellCheck={false} />
                </label>
                <CredentialField label="Password" defined={status.hasPassword} value={password} onChange={setPassword} />
                <CredentialField label="Terminal Key" defined={status.hasTerminalKey} value={terminalKey} onChange={setTerminalKey} />
                <p className="pagaqui-config-note">Para manter a password e a chave atuais, deixa os respetivos campos em branco.</p>
              </div>
            </section>
          </fieldset>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="pagaqui-save-bar">
            <div aria-live="polite">{saved && !error ? <span className="pagaqui-saved"><CheckCircle2 size={18} /> Configuração guardada.</span> : <span>As alterações são aplicadas ao guardar.</span>}</div>
            <button className="button button--primary" disabled={busy}><Save size={17} />{busy ? 'A guardar…' : 'Guardar alterações'}</button>
          </div>
        </form>
      )}
    </div>
  );
}
